"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { KbvPluginService } from "@/services/kbvPluginService";
import { useRemoteLeagueSearch, fetchHotLeagues } from "@/hooks/useRemoteLeagueSearch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import { TAB_LABELS } from "@/constants";

const getGroupLabel = (group, index) => {
  const key = group.groupName || `group_${group.id}`;
  if (TAB_LABELS[key]) return TAB_LABELS[key];

  const letter = String(key).match(/(?:group[_\s]*)([a-z])$/i)?.[1]?.toUpperCase();
  if (letter) return `Bảng ${letter}`;

  const raw = group.groupName || `Bảng ${String.fromCharCode(65 + index)}`;
  return raw.replace(/\bGroup\b/gi, "Bảng");
};

const getCupRankStyle = (rank) => {
  if (rank <= 2) return "bg-[#22c55e] text-white";
  if (rank === 3) return "bg-[#ef4444] text-white";
  return "bg-[#9ca3af] text-white";
};

export default function BangXepHangPageV2() {
  const COLLAPSED_ITEMS_COUNT = 10;
  const [hotLeagues, setHotLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  
  const [standingsData, setStandingsData] = useState(null);
  const [isStandingsLoading, setIsStandingsLoading] = useState(false);
  
  const dropdownRef = useRef(null);
  const debouncedQuery = useDebouncedValue(searchQuery, 350).trim();

  // Search logic reuse
  const { results: remoteLeagues, isLoading: isSearchLoading } = useRemoteLeagueSearch({
    enabled: isDropdownOpen,
    keyword: debouncedQuery,
  });

  // Initial load: Hot Leagues
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchHotLeagues();
      if (cancelled) return;
      setHotLeagues(list || []);
      if (list && list.length > 0 && !selectedLeague) {
        const defaultLeague = list.find((league) => Number(league?.id) === 1639) || list[0];
        setSelectedLeague(defaultLeague);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch standings when league changes
  useEffect(() => {
    if (!selectedLeague?.id) return;
    
    let cancelled = false;
    setIsStandingsLoading(true);
    (async () => {
      try {
        const data = await KbvPluginService.getLeagueStandings(selectedLeague.id);
        if (cancelled) return;
        setStandingsData(data?.result);
      } catch (error) {
        console.error("Fetch standings error:", error);
        setStandingsData(null);
      } finally {
        if (!cancelled) setIsStandingsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedLeague?.id]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayLeagues = useMemo(() => {
    if (debouncedQuery) return remoteLeagues;
    return hotLeagues;
  }, [debouncedQuery, remoteLeagues, hotLeagues]);

  const isCup = useMemo(
    () =>
      Array.isArray(standingsData?.roundScoreItems) &&
      standingsData.roundScoreItems.length > 0,
    [standingsData]
  );

  const cupGroups = useMemo(() => {
    if (!isCup) return [];
    return standingsData.roundScoreItems[0]?.groupScoreItems || [];
  }, [isCup, standingsData]);

  useEffect(() => {
    setActiveGroupIndex(0);
    setIsExpanded(false);
  }, [selectedLeague?.id, standingsData]);

  const handleSelect = (league) => {
    setSelectedLeague(league);
    setIsDropdownOpen(false);
    setSearchQuery("");
    setIsExpanded(false);
    setActiveGroupIndex(0);
  };

  const renderTable = () => {
    if (isStandingsLoading) {
      return (
        <div className="flex justify-center py-10">
          <Loading />
        </div>
      );
    }

    if (!standingsData) return null;

    const list = isCup
      ? cupGroups[activeGroupIndex]?.scoreItems || []
      : standingsData?.totalStandings || [];

    if (list.length === 0) return (
      <NoData>
        <span className="text-xs text-gray-500">Không có dữ liệu bảng xếp hạng</span>
      </NoData>
    );

    const visibleList = isExpanded ? list : list.slice(0, COLLAPSED_ITEMS_COUNT);
    const showToggleButton = !isCup && list.length > COLLAPSED_ITEMS_COUNT;

    if (isCup) {
      const sortedCupList = [...list].sort((a, b) => {
        const pointsA = Number(a.integral ?? a.winCount * 3 + a.drawCount);
        const pointsB = Number(b.integral ?? b.winCount * 3 + b.drawCount);
        if (pointsB !== pointsA) return pointsB - pointsA;

        const gdA = Number(a.goalDifference ?? a.totalGoalDifference ?? 0);
        const gdB = Number(b.goalDifference ?? b.totalGoalDifference ?? 0);
        if (gdB !== gdA) return gdB - gdA;

        return Number(b.getScore ?? 0) - Number(a.getScore ?? 0);
      });

      return (
        <>
          <div className="grid grid-cols-4 gap-1.5">
            {cupGroups.map((group, index) => (
              <button
                key={group.id || index}
                type="button"
                onClick={() => setActiveGroupIndex(index)}
                className={`rounded-full px-2 py-0.5 text-center text-[10px] font-semibold transition-colors ${
                  activeGroupIndex === index
                    ? "bg-[#FFB800] text-white"
                    : "bg-[#F0F2F5] text-[#4A4A4A] hover:bg-[#e8eaed]"
                }`}
              >
                {getGroupLabel(group, index)}
              </button>
            ))}
          </div>

          <div className="mt-2 w-full overflow-hidden rounded-lg border border-[#e5e7eb]">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[240px] table-fixed border-separate border-spacing-0 text-[11px] sm:text-[12px]">
                <thead>
                  <tr className="bg-[#1f2937] text-white">
                    <th className="w-[65%] rounded-tl-lg py-2 pl-8 text-left font-semibold">Đội bóng</th>
                    <th className="w-[20%] py-2 text-center font-semibold">Trận</th>
                    <th className="w-[15%] rounded-tr-lg py-2 text-center font-semibold">Điểm</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCupList.map((row, idx) => {
                    const team = row.teams || {};
                    const rankNumber = idx + 1;
                    const points = row.integral ?? row.winCount * 3 + row.drawCount;
                    const isTopOne = rankNumber === 1;

                    return (
                      <tr
                        key={row.id || idx}
                        className={`border-b border-[#eceef1] transition-colors hover:bg-[#f9fafb] ${isTopOne ? "bg-[#fff8e1]" : ""}`}
                      >
                        <td className="border-b border-[#eceef1] py-2 pr-1">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${getCupRankStyle(rankNumber)}`}
                            >
                              {rankNumber}
                            </span>
                            <div
                              role="link"
                              tabIndex={0}
                              onClick={() => window.location.assign(`/doi-bong/${team.slug || team.id}`)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  window.location.assign(`/doi-bong/${team.slug || team.id}`);
                                }
                              }}
                              className="group flex min-w-0 flex-1 cursor-pointer items-center gap-1.5"
                            >
                              {team.logo && (
                                <img
                                  src={team.logo}
                                  alt=""
                                  width={16}
                                  height={16}
                                  className="shrink-0 object-contain"
                                />
                              )}
                              <span className={`truncate text-[11px] transition-colors group-hover:text-blue-600 sm:text-[12px] ${isTopOne ? "font-bold text-[#b45309]" : "font-semibold text-[#2e313a]"}`}>
                                {team.nameVi || team.name}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className={`border-b border-[#eceef1] py-2 text-center font-medium ${isTopOne ? "font-bold text-[#b45309]" : "text-[#2e313a]"}`}>
                          {row.totalCount}
                        </td>
                        <td className={`border-b border-[#eceef1] py-2 text-center text-[12px] sm:text-[13px] ${isTopOne ? "font-extrabold text-[#b45309]" : "font-bold text-[#2e313a]"}`}>
                          {points}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="w-full overflow-hidden rounded-lg border border-[#e5e7eb]">
          <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[340px] table-fixed border-separate border-spacing-0 text-[11px] sm:min-w-0 sm:text-[12px]">
          <thead>
            <tr className="bg-[#f3f4f6] text-[#30323d]">
              <th className="w-[10%] rounded-tl-lg py-2 text-center font-semibold">#</th>
              <th className="w-[50%] py-2 pl-2 text-left font-semibold">Đội bóng</th>
              <th className="w-[13%] py-2 text-center font-semibold">P</th>
              <th className="w-[13%] py-2 text-center font-semibold">GD</th>
              <th className="w-[14%] rounded-tr-lg py-2 text-center font-semibold">PTS</th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map((row, idx) => {
              const team = row.teams || {};
              const goalDiff = Number(row.goalDifference ?? row.totalGoalDifference ?? row.goalsDiff ?? 0);
              const rankNumber = Number(row.rank ?? idx + 1);
              const isTopOne = rankNumber === 1;
              const isRelegation = rankNumber >= list.length - 2;
              return (
                <tr
                  key={row.id || idx}
                  className={`border-b border-[#eceef1] transition-colors hover:bg-[#f9fafb] ${isTopOne ? "bg-[#fff8e1]" : ""} ${isRelegation ? "bg-[#fef2f2]" : ""}`}
                >
                  <td className={`border-b border-[#eceef1] py-2 text-center text-[10px] font-medium leading-none ${isTopOne ? "text-[#b45309]" : ""} ${isRelegation ? "text-[#b91c1c]" : "text-[#2e313a]"}`}>
                    {row.rank}
                  </td>
                  <td className="max-w-0 border-b border-[#eceef1] py-2 pr-1">
                    <div
                        role="link"
                        tabIndex={0}
                        onClick={() => window.location.assign(`/doi-bong/${team.slug || team.id}`)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            window.location.assign(`/doi-bong/${team.slug || team.id}`);
                          }
                        }}
                        className="group flex w-full cursor-pointer items-center gap-1.5"
                    >
                      {team.logo && (
                        <img 
                            src={team.logo} 
                            alt="" 
                            width={16} 
                            height={16} 
                            className="shrink-0 object-contain" 
                        />
                      )}
                      <span className="truncate no-underline text-[11px] font-semibold text-[#2e313a] transition-colors group-hover:text-blue-600 group-hover:no-underline sm:text-[12px]">
                        {team.name}
                      </span>
                    </div>
                  </td>
                  <td className="border-b border-[#eceef1] py-2 text-center font-medium text-[#2e313a]">
                    {row.totalCount}
                  </td>
                  <td className={`border-b border-[#eceef1] py-2 text-center font-medium ${goalDiff >= 0 ? "text-[#0f766e]" : "text-[#dc2626]"}`}>
                    {goalDiff > 0 ? `+${goalDiff}` : goalDiff}
                  </td>
                  <td className="border-b border-[#eceef1] py-2 text-center text-[12px] font-bold text-[#2e313a] sm:text-[13px]">
                    {row.integral}
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
          </div>
        </div>
        {showToggleButton && (
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="mt-2 w-full rounded-lg border border-gray-200 py-2 text-[13px] font-semibold text-stroke-dark transition-colors hover:bg-gray-50 sm:text-[14px]"
          >
            {isExpanded ? "Thu gọn" : "Mở rộng"}
          </button>
        )}
      </>
    );
  };

  return (
    <aside className="min-h-87.5 w-full self-start overflow-hidden rounded border border-gray-100 bg-white shadow-sm" aria-label="Ranking per tournament">
      <div className="flex flex-col gap-2 p-2 sm:p-2.5">
        <div className="flex items-center gap-2">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#fef3c7]">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-[#d97706]" fill="currentColor" aria-hidden="true">
                <path d="M8.5 3a1 1 0 0 0-1 1v1H5.75A1.75 1.75 0 0 0 4 6.75V8a4.5 4.5 0 0 0 4 4.472A4.99 4.99 0 0 0 11 14.91V17H8.5a1 1 0 1 0 0 2h7a1 1 0 1 0 0-2H13v-2.09a4.99 4.99 0 0 0 3-2.438A4.5 4.5 0 0 0 20 8V6.75A1.75 1.75 0 0 0 18.25 5H16.5V4a1 1 0 0 0-1-1h-7ZM6 8V7h1.5v2.997A2.5 2.5 0 0 1 6 8Zm10.5-.003V7H18v1a2.5 2.5 0 0 1-1.5 1.997V7.997Z" />
              </svg>
            </span>
            <div className="text-[16px] font-bold text-stroke-dark sm:text-[17px]">Bảng xếp hạng</div>
        </div>

        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex h-8 w-full items-center justify-between rounded-full border border-gray-200 bg-white px-3 text-[13px] transition-all hover:border-primary sm:px-4 sm:text-[14px]"
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {selectedLeague?.logo && (
                        <img 
                            src={selectedLeague.logo} 
                            alt="" 
                            width={14} 
                            height={14} 
                            className="object-contain" 
                        />
                    )}
                    <span className="truncate font-medium text-stroke-dark">
                        {selectedLeague?.nameVN || selectedLeague?.name || "Chọn giải đấu..."}
                    </span>
                </div>
                <img 
                    src="/arrow-up.svg" 
                    alt="" 
                    width={16}
                    height={16}
                    className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-0' : 'rotate-180'}`}
                />
            </button>

            {isDropdownOpen && (
                <div className="absolute left-0 right-0 top-[40px] z-[100] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
                    <div className="border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white p-2.5">
                        <div className="relative">
                            <input
                                autoFocus
                                type="text"
                                placeholder="Tìm kiếm giải đấu..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-[13px] text-gray-700 outline-none transition-all placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:text-[14px]"
                            />
                            <img 
                                src="/search.svg" 
                                alt="" 
                                width={14}
                                height={14}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 opacity-45" 
                            />
                        </div>
                    </div>
                    <div className="max-h-[50vh] overflow-y-auto py-1 league-filter-scrollbar sm:max-h-[240px]">
                        {isSearchLoading ? (
                            <div className="p-4 flex justify-center"><Loading /></div>
                        ) : displayLeagues.map((league) => (
                            <button
                                key={league.id}
                                onClick={() => handleSelect(league)}
                                className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors sm:px-3.5 ${selectedLeague?.id === league.id ? "bg-blue-50/70 text-blue-700" : "text-gray-700 hover:bg-gray-50"}`}
                            >
                                {league.logo && (
                                    <img 
                                        src={league.logo} 
                                        alt="" 
                                        width={16} 
                                        height={16} 
                                        className="object-contain" 
                                    />
                                )}
                                <span className={`truncate text-[13px] sm:text-[14px] ${selectedLeague?.id === league.id ? "font-semibold" : "font-medium"}`}>{league.nameVN || league.name}</span>
                                {selectedLeague?.id === league.id && (
                                    <img 
                                        src="/icons/check-icon.svg" 
                                        alt="" 
                                        width={14}
                                        height={14}
                                        className="ml-auto opacity-90" 
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div>
            {renderTable()}
        </div>

        {/* {standingsData && standingsData.totalStandings?.length > 0 && (
            <div className="text-center pt-1">
                <a 
                    href={`/giai-dau/${selectedLeague?.slug || selectedLeague?.id}?tab=bxh`}
                    className="text-[14px] font-semibold text-primary hover:underline"
                >
                    BXH chi tiết
                </a>
            </div>
        )} */}
      </div>
    </aside>
  );
}
